"use client"

import { useLayoutEffect } from 'react'
import { usePathname } from 'next/navigation'

const BOOT_VISIBLE_MS = 3000
const WALLET_TELEPORT_EXIT_MS = 820
const NAVIGATION_FAILSAFE_MS = 15000
const COMMIT_EVENT = 'ql7:page-boot-route-committed'

function exempt(pathname) {
  return /^\/(?:forum|tma)(?:\/|$)/.test(String(pathname || '/'))
}

// This controller never renders the splash itself. The SSR shell is already
// before Providers/children and its head CSS/route decision run before paint.
export default function GlobalPageBootRuntime() {
  const pathname = usePathname()

  useLayoutEffect(() => {
    const shell = document.getElementById('ql7-global-page-boot')
    if (!shell) return undefined
    const html = document.documentElement
    const clock = () => performance.now()
    const state = {
      current: window.location.pathname,
      destination: '',
      started: Number(window.__QL7_PAGE_BOOT_STARTED_AT__) || clock(),
      waitForCommit: false,
      visibleTimer: 0,
      exitTimer: 0,
      failsafe: 0,
    }

    const cancelTimers = () => {
      window.clearTimeout(state.visibleTimer)
      window.clearTimeout(state.exitTimer)
      window.clearTimeout(state.failsafe)
      state.visibleTimer = 0
      state.exitTimer = 0
      state.failsafe = 0
    }
    const warmCore = () => {
      if (document.querySelector('[data-ql7-page-boot-preload="1"]')) return
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'image'
      link.href = '/ql7-transaction-core.png'
      link.setAttribute('fetchpriority', 'high')
      link.setAttribute('data-ql7-page-boot-preload', '1')
      document.head.appendChild(link)
    }
    const hideImmediately = () => {
      cancelTimers()
      state.destination = ''
      state.waitForCommit = false
      shell.dataset.phase = 'hidden'
      html.setAttribute('data-ql7-page-boot-route', 'exempt')
      html.removeAttribute('data-ql7-page-boot-active')
    }
    const exit = () => {
      if (state.waitForCommit || shell.dataset.phase !== 'show') return
      shell.dataset.phase = 'exit'
      html.removeAttribute('data-ql7-page-boot-active')
      state.exitTimer = window.setTimeout(() => {
        shell.dataset.phase = 'hidden'
        state.destination = ''
      }, WALLET_TELEPORT_EXIT_MS)
    }
    const scheduleExit = () => {
      window.clearTimeout(state.visibleTimer)
      const remaining = Math.max(0, BOOT_VISIBLE_MS - (clock() - state.started))
      state.visibleTimer = window.setTimeout(exit, remaining)
    }
    const show = (destination, alreadyStarted = false) => {
      cancelTimers()
      state.destination = destination
      state.started = alreadyStarted ? state.started : clock()
      state.waitForCommit = destination !== state.current
      warmCore()
      html.setAttribute('data-ql7-page-boot-route', 'enabled')
      html.setAttribute('data-ql7-page-boot-active', '1')
      shell.dataset.phase = 'show'
      if (state.waitForCommit) {
        // A prevented/aborted navigation must never leave a permanent black veil.
        state.failsafe = window.setTimeout(() => {
          state.waitForCommit = false
          state.started = clock() - BOOT_VISIBLE_MS
          scheduleExit()
        }, NAVIGATION_FAILSAFE_MS)
      } else {
        scheduleExit()
      }
    }
    const onNavigationClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = event.target?.closest?.('a[href]')
      if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return
      let url
      try { url = new URL(anchor.href, window.location.href) } catch { return }
      if (url.origin !== window.location.origin || url.pathname === state.current) return
      if (exempt(url.pathname)) return
      show(url.pathname)
    }
    const onPopstate = () => {
      const next = window.location.pathname
      if (exempt(next)) { hideImmediately(); return }
      if (next !== state.current) show(next)
    }
    const onCommitted = (event) => {
      const next = String(event?.detail?.pathname || window.location.pathname)
      state.current = next
      if (exempt(next)) { hideImmediately(); return }
      if (state.destination !== next && state.destination !== '') {
        // Redirects keep the prepaint curtain but advance the target.
        state.destination = next
      }
      if (shell.dataset.phase === 'hidden' || !state.destination) {
        if (shell.dataset.phase !== 'show') show(next)
      }
      state.waitForCommit = false
      window.clearTimeout(state.failsafe)
      state.failsafe = 0
      scheduleExit()
    }

    document.addEventListener('click', onNavigationClick, true)
    window.addEventListener('popstate', onPopstate)
    window.addEventListener(COMMIT_EVENT, onCommitted)
    if (exempt(state.current)) hideImmediately()
    else show(state.current, true)

    return () => {
      cancelTimers()
      document.removeEventListener('click', onNavigationClick, true)
      window.removeEventListener('popstate', onPopstate)
      window.removeEventListener(COMMIT_EVENT, onCommitted)
    }
  }, [])

  useLayoutEffect(() => {
    if (!pathname) return
    window.dispatchEvent(new CustomEvent(COMMIT_EVENT, { detail: { pathname } }))
  }, [pathname])

  return null
}
